import React, { useState, useCallback } from 'react';
import TransformsEditor from './TransformsEditor';

// ─── Strategy Config ────────────────────────────────────────────────────────

const STRATEGY_META = {
  CSV_POSITIONAL: { label: 'CSV', color: '#2563EB', bg: '#EFF6FF', icon: '⊞' },
  OCR_AI:         { label: 'OCR',  color: '#D97706', bg: '#FFFBEB', icon: '◉' },
  MANUAL:         { label: 'Manual', color: '#059669', bg: '#ECFDF5', icon: '✎' },
  SYSTEM:         { label: 'System', color: '#6B7280', bg: '#F3F4F6', icon: '⚙' },
};

const TYPE_OPTIONS = ['STRING', 'INTEGER', 'LONG', 'DATE'];
const AVAILABLE_STRATEGIES = ['CSV_POSITIONAL', 'OCR_AI', 'MANUAL'];

// ─── Sub-components ─────────────────────────────────────────────────────────

const StrategyChip = ({ strategy, compact, onRemove }) => {
  const meta = STRATEGY_META[strategy.strategyType] || STRATEGY_META.MANUAL;
  const pos = strategy.strategyType === 'CSV_POSITIONAL' && strategy.config?.position
    ? `:${strategy.config.position}` : '';

  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: compact ? '2px 8px' : '3px 10px',
      borderRadius: 20,
      fontSize: compact ? '0.7rem' : '0.75rem',
      fontWeight: 600,
      letterSpacing: '0.02em',
      color: meta.color,
      backgroundColor: meta.bg,
      border: `1px solid ${meta.color}22`,
      whiteSpace: 'nowrap',
      transition: 'all 0.15s ease',
    }}>
      <span style={{ fontSize: compact ? '0.65rem' : '0.7rem' }}>{meta.icon}</span>
      {meta.label}{pos}
      {onRemove && (
        <button onClick={(e) => { e.stopPropagation(); onRemove(); }} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: meta.color, fontSize: '0.85rem', padding: 0, marginLeft: 2,
          opacity: 0.6, lineHeight: 1,
        }} title="Quitar estrategia">×</button>
      )}
    </span>
  );
};

const TagInput = ({ tags = [], onChange, placeholder }) => {
  const [input, setInput] = useState('');

  const addTag = () => {
    const val = input.trim();
    if (val && !tags.includes(val)) {
      onChange([...tags, val]);
      setInput('');
    }
  };

  return (
    <div style={{
      display: 'flex', flexWrap: 'wrap', gap: 4, padding: '6px 8px',
      border: '1px solid #D1D5DB', borderRadius: 8, backgroundColor: '#fff',
      minHeight: 36, alignItems: 'center',
    }}>
      {tags.map((tag, i) => (
        <span key={i} style={{
          display: 'inline-flex', alignItems: 'center', gap: 3,
          padding: '2px 8px', borderRadius: 12,
          fontSize: '0.75rem', backgroundColor: '#F3F4F6',
          color: '#374151', border: '1px solid #E5E7EB',
        }}>
          {tag}
          <button onClick={() => onChange(tags.filter((_, j) => j !== i))} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#9CA3AF', fontSize: '0.8rem', padding: 0, lineHeight: 1,
          }}>×</button>
        </span>
      ))}
      <input
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
        onBlur={addTag}
        placeholder={tags.length === 0 ? placeholder : ''}
        style={{
          border: 'none', flex: 1, minWidth: 80,
          fontSize: '0.8rem', color: '#374151', backgroundColor: 'transparent',
        }}
      />
    </div>
  );
};

// ─── Strategy Editor Panel ──────────────────────────────────────────────────

const StrategyEditor = ({ strategy, onChange, onRemove, seriesHasDocuments = false }) => {
  const meta = STRATEGY_META[strategy.strategyType] || STRATEGY_META.MANUAL;
  const config = strategy.config || {};

  const updateConfig = (key, value) => {
    onChange({ ...strategy, config: { ...config, [key]: value } });
  };

  const sectionStyle = {
    padding: '12px 16px', borderRadius: 10,
    border: `1px solid ${meta.color}20`,
    backgroundColor: meta.bg + '80', marginBottom: 8,
  };

  const labelStyle = {
    display: 'block', fontSize: '0.7rem', fontWeight: 600,
    color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 4,
  };

  const inputStyle = {
    width: '100%', padding: '6px 10px', borderRadius: 6,
    border: '1px solid #D1D5DB', fontSize: '0.8rem',
    color: '#1F2937', backgroundColor: '#fff', boxSizing: 'border-box',
  };

  const textareaStyle = { ...inputStyle, minHeight: 60, resize: 'vertical', fontFamily: 'inherit' };

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <StrategyChip strategy={strategy} />
        <button onClick={onRemove} style={{
          background: 'none', border: '1px solid #FCA5A5', borderRadius: 6,
          color: '#DC2626', fontSize: '0.7rem', padding: '3px 10px', cursor: 'pointer',
        }}>Quitar</button>
      </div>

      {strategy.strategyType === 'CSV_POSITIONAL' && (
        <div>
          <label style={labelStyle}>Posición en CSV (1-based)</label>
          <input type="number" min="1" value={config.position || ''} style={{ ...inputStyle, width: 80 }}
            onChange={(e) => updateConfig('position', parseInt(e.target.value) || null)} />
        </div>
      )}

      {strategy.strategyType === 'OCR_AI' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <label style={labelStyle}>Pista para la IA</label>
            <textarea value={config.hint || ''} style={textareaStyle}
              placeholder="Ej: Número del remito, suele aparecer en la parte superior..."
              onChange={(e) => updateConfig('hint', e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Sinónimos</label>
            <TagInput tags={config.synonyms || []} placeholder="Agregar sinónimo + Enter"
              onChange={(v) => updateConfig('synonyms', v)} />
          </div>
          <div>
            <label style={labelStyle}>Ejemplos de valores</label>
            <TagInput tags={config.examples || []} placeholder="Agregar ejemplo + Enter"
              onChange={(v) => updateConfig('examples', v)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <label style={labelStyle}>Patrón regex</label>
              <input value={config.pattern || ''} style={inputStyle} placeholder="^[A-Z]-\d+$"
                onChange={(e) => updateConfig('pattern', e.target.value)} />
            </div>
            <div>
              <label style={labelStyle}>Instrucciones de extracción</label>
              <input value={config.extractionInstructions || ''} style={inputStyle}
                placeholder="Si hay varios valores, elegir el más cercano a..."
                onChange={(e) => updateConfig('extractionInstructions', e.target.value)} />
            </div>
          </div>

          <TransformsEditor
            transforms={config.transforms || []}
            onChange={(newTransforms) => updateConfig('transforms', newTransforms)}
            seriesHasDocuments={seriesHasDocuments}
          />
        </div>
      )}

      {strategy.strategyType === 'MANUAL' && (
        <p style={{ fontSize: '0.75rem', color: '#6B7280', margin: 0, fontStyle: 'italic' }}>
          Sin configuración adicional — el valor se carga manualmente.
        </p>
      )}
    </div>
  );
};

// ─── Field Row (collapsed) ──────────────────────────────────────────────────

const FieldRow = ({ field, index, isExpanded, onToggle, onMoveUp, onMoveDown, onDelete, isFirst, isLast, hasDocuments }) => {
  const rowStyle = {
    display: 'grid',
    gridTemplateColumns: '30px 1fr 1fr 80px auto 30px',
    alignItems: 'center',
    gap: 8,
    padding: '10px 14px',
    cursor: 'pointer',
    backgroundColor: isExpanded ? '#F8FAFC' : '#fff',
    borderBottom: isExpanded ? 'none' : '1px solid #F1F5F9',
    transition: 'background-color 0.15s ease',
    borderRadius: isExpanded ? '10px 10px 0 0' : 0,
  };

  return (
    <div style={rowStyle} onClick={onToggle}
      onMouseEnter={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#F8FAFC'; }}
      onMouseLeave={(e) => { if (!isExpanded) e.currentTarget.style.backgroundColor = '#fff'; }}
    >
      {/* Reorder arrows (no order number — avoids confusion with duplicates) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }} onClick={(e) => e.stopPropagation()}>
        <button disabled={isFirst} onClick={onMoveUp} style={{
          background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer',
          fontSize: '0.7rem', color: isFirst ? '#E2E8F0' : '#94A3B8', padding: 0, lineHeight: 1,
        }}>▲</button>
        <button disabled={isLast} onClick={onMoveDown} style={{
          background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer',
          fontSize: '0.7rem', color: isLast ? '#E2E8F0' : '#94A3B8', padding: 0, lineHeight: 1,
        }}>▼</button>
      </div>

      {/* Name + Label */}
      <div style={{ overflow: 'hidden' }}>
        <div style={{
          fontSize: '0.85rem', fontWeight: 600, color: '#1E293B',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {field.label || field.name}
          {field.required && (
            <span style={{
              fontSize: '0.6rem', fontWeight: 700, color: '#DC2626',
              marginLeft: 6, verticalAlign: 'super',
            }}>REQ</span>
          )}
        </div>
        <div style={{
          fontSize: '0.7rem', color: '#94A3B8',
          fontFamily: '"SF Mono", "Fira Code", monospace',
        }}>{field.name}</div>
      </div>

      {/* Type + mask */}
      <div>
        <span style={{
          fontSize: '0.7rem', fontWeight: 500, color: '#64748B',
          padding: '2px 8px', borderRadius: 4, backgroundColor: '#F1F5F9',
          fontFamily: '"SF Mono", "Fira Code", monospace',
        }}>{field.type || 'STRING'}</span>
        {field.mask && (
          <span style={{ fontSize: '0.65rem', color: '#94A3B8', marginLeft: 6 }}>{field.mask}</span>
        )}
      </div>

      {/* Fuentes (strategy chips) */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {(field.inputStrategies || []).map((s, i) => (
          <StrategyChip key={i} strategy={s} compact />
        ))}
        {(!field.inputStrategies || field.inputStrategies.length === 0) && (
          <span style={{ fontSize: '0.7rem', color: '#CBD5E1', fontStyle: 'italic' }}>—</span>
        )}
      </div>

      {/* Delete */}
      <div onClick={(e) => e.stopPropagation()}>
        {!hasDocuments ? (
          <button onClick={onDelete} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#CBD5E1', fontSize: '0.9rem', padding: '2px 4px', lineHeight: 1,
            borderRadius: 4, transition: 'color 0.15s',
          }}
            onMouseEnter={(e) => e.currentTarget.style.color = '#EF4444'}
            onMouseLeave={(e) => e.currentTarget.style.color = '#CBD5E1'}
            title="Eliminar campo"
          >×</button>
        ) : <span style={{ width: 20 }} />}
      </div>
    </div>
  );
};

// ─── Expanded Editor Panel ──────────────────────────────────────────────────

const FieldEditor = ({ field, onChange, hasDocuments }) => {
  const [newStrategy, setNewStrategy] = useState('');

  const updateField = (key, value) => {
    onChange({ ...field, [key]: value });
  };

  const updateStrategy = (index, updatedStrategy) => {
    const strategies = [...(field.inputStrategies || [])];
    strategies[index] = updatedStrategy;
    onChange({ ...field, inputStrategies: strategies });
  };

  const removeStrategy = (index) => {
    const strategies = (field.inputStrategies || []).filter((_, i) => i !== index);
    onChange({ ...field, inputStrategies: strategies });
  };

  const addStrategy = () => {
    if (!newStrategy) return;
    const exists = (field.inputStrategies || []).some(s => s.strategyType === newStrategy);
    if (exists) return;
    const config = newStrategy === 'CSV_POSITIONAL' ? { position: (field.displayOrder || 1) } : {};
    onChange({
      ...field,
      inputStrategies: [...(field.inputStrategies || []), { strategyType: newStrategy, config }]
    });
    setNewStrategy('');
  };

  const existingTypes = (field.inputStrategies || []).map(s => s.strategyType);
  const availableToAdd = AVAILABLE_STRATEGIES.filter(s => !existingTypes.includes(s));

  const labelStyle = {
    display: 'block', fontSize: '0.7rem', fontWeight: 600,
    color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.05em',
    marginBottom: 4,
  };

  const inputStyle = {
    width: '100%', padding: '7px 10px', borderRadius: 8,
    border: '1px solid #D1D5DB', fontSize: '0.8rem',
    color: '#1F2937', backgroundColor: '#fff', boxSizing: 'border-box',
    transition: 'border-color 0.15s ease',
  };

  const disabledInputStyle = {
    ...inputStyle, backgroundColor: '#F9FAFB', cursor: 'not-allowed', color: '#9CA3AF',
  };

  return (
    <div style={{
      backgroundColor: '#F8FAFC',
      borderRadius: '0 0 10px 10px',
      borderTop: '1px solid #E2E8F0',
      padding: '16px 20px 20px',
      marginBottom: 2,
    }}>
      {/* ── Field Properties ─────────────────────────── */}
      <div style={{
        fontSize: '0.7rem', fontWeight: 700, color: '#94A3B8',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #E2E8F0',
      }}>Propiedades del campo</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Nombre (ID)</label>
          <input value={field.name || ''} style={hasDocuments ? disabledInputStyle : {
            ...inputStyle, fontFamily: '"SF Mono", "Fira Code", monospace', fontSize: '0.78rem',
          }}
            disabled={hasDocuments}
            title={hasDocuments ? "No se puede cambiar con documentos existentes" : ""}
            onChange={(e) => updateField('name', e.target.value)} />
        </div>
        <div>
          <label style={labelStyle}>Etiqueta</label>
          <input value={field.label || ''} style={inputStyle}
            onChange={(e) => updateField('label', e.target.value)} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Tipo de dato</label>
          <select value={field.type || 'STRING'} style={hasDocuments ? { ...disabledInputStyle, cursor: 'not-allowed' } : { ...inputStyle, cursor: 'pointer' }}
            disabled={hasDocuments}
            title={hasDocuments ? "No se puede cambiar con documentos existentes" : ""}
            onChange={(e) => updateField('type', e.target.value)}>
            {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Máscara {(field.type || 'STRING') !== 'DATE' && <span style={{ fontWeight: 'normal', color: '#aaa' }}>(solo para DATE)</span>}</label>
          <input value={field.mask || ''} style={(field.type || 'STRING') !== 'DATE' ? disabledInputStyle : inputStyle}
            disabled={(field.type || 'STRING') !== 'DATE'}
            placeholder="yyyy-MM-dd"
            onChange={(e) => updateField('mask', e.target.value)} />
        </div>
      </div>

      {/* Checkboxes en grid 2 cols, mismo estilo que Configuración de Workflow */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
        {[
          ['required', 'Metadato requerido', 'Debe estar presente para importar el documento'],
          ['uiFilterable', 'Permitir filtro en buscador', 'Aparece como opción de filtro en búsquedas'],
          ['uiSortable', 'Permitir ordenar', 'Se puede ordenar la grilla por este campo'],
          ['uiEditable', 'Campo editable', 'El revisor puede modificar este valor'],
        ].map(([key, label, hint]) => (
          <label key={key} style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
            padding: '6px 10px', borderRadius: 8, transition: 'background-color 0.1s',
          }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <input type="checkbox" checked={!!field[key]}
              onChange={(e) => updateField(key, e.target.checked)}
              style={{ accentColor: '#2563EB', width: 15, height: 15, marginTop: 2, cursor: 'pointer', flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: '0.8rem', color: '#374151', fontWeight: 500 }}>{label}</div>
              <div style={{ fontSize: '0.68rem', color: '#9CA3AF' }}>{hint}</div>
            </div>
          </label>
        ))}
      </div>

      {/* Descripción */}
      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Descripción <span style={{ fontWeight: 'normal', color: '#aaa' }}>(usada en el prompt OCR)</span></label>
        <input value={field.description || ''} style={inputStyle}
          placeholder="Descripción del campo..."
          onChange={(e) => updateField('description', e.target.value)} />
      </div>

      {/* ── Fuentes de entrada ──────────────────────────── */}
      <div style={{
        fontSize: '0.7rem', fontWeight: 700, color: '#94A3B8',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid #E2E8F0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span>Fuentes de entrada</span>
        {availableToAdd.length > 0 && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <select value={newStrategy} onChange={(e) => setNewStrategy(e.target.value)}
              style={{
                padding: '3px 8px', borderRadius: 6, border: '1px solid #D1D5DB',
                fontSize: '0.72rem', color: '#4B5563', cursor: 'pointer', backgroundColor: '#fff',
              }}>
              <option value="">Agregar fuente...</option>
              {availableToAdd.map(s => (
                <option key={s} value={s}>{STRATEGY_META[s]?.label || s}</option>
              ))}
            </select>
            <button onClick={addStrategy} disabled={!newStrategy} style={{
              padding: '3px 10px', borderRadius: 6, border: 'none',
              backgroundColor: newStrategy ? '#2563EB' : '#E5E7EB',
              color: newStrategy ? '#fff' : '#9CA3AF',
              fontSize: '0.72rem', fontWeight: 600, cursor: newStrategy ? 'pointer' : 'default',
              transition: 'all 0.15s ease',
            }}>+</button>
          </div>
        )}
      </div>

      {(field.inputStrategies || []).length === 0 && (
        <p style={{
          fontSize: '0.78rem', color: '#94A3B8', fontStyle: 'italic',
          textAlign: 'center', padding: '12px 0',
        }}>
          Sin fuentes configuradas. Agregá al menos una para que el campo pueda recibir datos.
        </p>
      )}

      {(field.inputStrategies || []).map((s, i) => (
        <StrategyEditor key={`${s.strategyType}-${i}`} strategy={s}
          onChange={(updated) => updateStrategy(i, updated)}
          onRemove={() => removeStrategy(i)}
          seriesHasDocuments={hasDocuments} />
      ))}
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────────────

const MetadataFieldsEditor = ({ fields = [], onFieldsChange, onDeleteField, hasDocuments = false }) => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleExpand = useCallback((index) => {
    setExpandedIndex(prev => prev === index ? null : index);
  }, []);

  const updateField = useCallback((index, updatedField) => {
    const newFields = [...fields];
    newFields[index] = {
      ...updatedField,
      nombre: updatedField.name,
      tipo: updatedField.type,
      posicion: updatedField.displayOrder,
    };
    onFieldsChange(newFields);
  }, [fields, onFieldsChange]);

  const moveField = useCallback((index, direction) => {
    const newFields = [...fields];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= newFields.length) return;
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    newFields.forEach((f, i) => {
      f.displayOrder = i + 1;
      f.posicion = i + 1;
      const csvStrategy = (f.inputStrategies || []).find(s => s.strategyType === 'CSV_POSITIONAL');
      if (csvStrategy) {
        csvStrategy.config = { ...csvStrategy.config, position: i + 1 };
      }
    });
    if (expandedIndex === index) setExpandedIndex(targetIndex);
    else if (expandedIndex === targetIndex) setExpandedIndex(index);
    onFieldsChange(newFields);
  }, [fields, onFieldsChange, expandedIndex]);

  const sorted = [...fields].sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));

  return (
    <div style={{
      borderRadius: 12,
      border: '1px solid #E2E8F0',
      backgroundColor: '#fff',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '30px 1fr 1fr 80px auto 30px',
        gap: 8,
        padding: '8px 14px',
        backgroundColor: '#F8FAFC',
        borderBottom: '1px solid #E2E8F0',
      }}>
        {['', 'Campo', 'Tipo', 'Fuentes', '', ''].map((h, i) => (
          <span key={i} style={{
            fontSize: '0.65rem', fontWeight: 700, color: '#94A3B8',
            textTransform: 'uppercase', letterSpacing: '0.06em',
          }}>{h}</span>
        ))}
      </div>

      {/* Field rows */}
      {sorted.map((field, index) => {
        const originalIndex = fields.indexOf(field);
        const isExpanded = expandedIndex === originalIndex;

        return (
          <div key={field.name || field.nombre || index}>
            <FieldRow
              field={field}
              index={index}
              isExpanded={isExpanded}
              onToggle={() => toggleExpand(originalIndex)}
              onMoveUp={() => moveField(originalIndex, -1)}
              onMoveDown={() => moveField(originalIndex, 1)}
              onDelete={() => onDeleteField && onDeleteField(field.name || field.nombre)}
              isFirst={index === 0}
              isLast={index === sorted.length - 1}
              hasDocuments={hasDocuments}
            />
            {isExpanded && (
              <FieldEditor
                field={field}
                onChange={(updated) => updateField(originalIndex, updated)}
                hasDocuments={hasDocuments}
              />
            )}
          </div>
        );
      })}

      {sorted.length === 0 && (
        <div style={{
          padding: '32px 20px', textAlign: 'center', color: '#94A3B8',
          fontSize: '0.85rem',
        }}>
          No hay campos definidos. Agregá el primero para empezar.
        </div>
      )}
    </div>
  );
};

export default MetadataFieldsEditor;
